import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

/**
 * Check if WhatsApp is configured for the tenant
 * @param {string} tenantId - The tenant ID
 * @param {string} userId - The user ID
 * @returns {Promise<boolean>} - True if configured, false otherwise
 */
export const checkWhatsAppConfig = async (tenantId, userId) => {
  try {
    const response = await axios.get(`${API_URL}/api/whatsapp-config/config`, {
      headers: {
        tenantid: tenantId,
        userid: userId
      }
    });

    return response.data.status === 'success' && response.data.data?.isActive;
  } catch (error) {
    if (error.response?.status === 404) {
      return false;
    }
    console.error('Error checking WhatsApp config:', error);
    return false;
  }
};

/**
 * Check if user can send regular messages (within 24-hour window)
 * @param {Date} lastIncomingMessageDate - Date of last incoming message from contact
 * @returns {boolean} - True if within 24-hour window
 */
export const canSendRegularMessage = (lastIncomingMessageDate) => {
  if (!lastIncomingMessageDate) return false;
  
  const now = new Date();
  const lastMessage = new Date(lastIncomingMessageDate);
  const hoursSinceLastMessage = (now - lastMessage) / (1000 * 60 * 60);
  
  return hoursSinceLastMessage < 24;
};

/**
 * Fetch all WhatsApp templates for the current tenant
 * @param {string} tenantId - The tenant ID
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} - Array of template objects
 */
export const fetchWhatsAppTemplates = async (tenantId, userId) => {
  try {
    const response = await axios.get(`${API_URL}/api/whatsapp-templates`, {
      headers: {
        tenantid: tenantId,
        userid: userId
      }
    });

    if (response.data.status === 'success') {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch templates');
  } catch (error) {
    console.error('Error fetching WhatsApp templates:', error);
    throw error;
  }
};

/**
 * Get approved templates only
 * @param {string} tenantId - The tenant ID
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} - Array of approved template objects
 */
export const fetchApprovedTemplates = async (tenantId, userId) => {
  try {
    const templates = await fetchWhatsAppTemplates(tenantId, userId);
    return templates.filter(template => template.status === 'APPROVED');
  } catch (error) {
    console.error('Error fetching approved templates:', error);
    throw error;
  }
};

/**
 * Send a WhatsApp template message
 * @param {Object} params - Template message parameters
 * @param {string} params.to - Recipient phone number
 * @param {string} params.templateName - Template name
 * @param {string} params.language - Template language code (e.g., 'en_US')
 * @param {Array} params.components - Template components with variables
 * @param {string} params.tenantId - The tenant ID
 * @param {string} params.userId - The user ID
 * @returns {Promise<Object>} - Response with message ID
 */
export const sendWhatsAppTemplate = async ({
  to,
  templateName,
  language = 'en_US',
  components = [],
  tenantId,
  userId
}) => {
  try {
    const response = await axios.post(
      `${API_URL}/api/whatsapp/send-template`,
      {
        to,
        templateName,
        language,
        components
      },
      {
        headers: {
          tenantid: tenantId,
          userid: userId
        }
      }
    );

    if (response.data.status === 'success') {
      return response.data;
    }
    throw new Error(response.data.message || 'Failed to send template');
  } catch (error) {
    console.error('Error sending WhatsApp template:', error);
    throw error;
  }
};

/**
 * Send a WhatsApp text message
 * @param {Object} params - Message parameters
 * @param {string} params.to - Recipient phone number
 * @param {string} params.message - Message text
 * @param {string} params.tenantId - The tenant ID
 * @param {string} params.userId - The user ID
 * @returns {Promise<Object>} - Response with message ID
 */
export const sendWhatsAppMessage = async ({ to, message, tenantId, userId }) => {
  try {
    const response = await axios.post(
      `${API_URL}/api/whatsapp/send-message`,
      { to, message },
      {
        headers: {
          tenantid: tenantId,
          userid: userId
        }
      }
    );

    if (response.data.status === 'success') {
      return response.data;
    }
    throw new Error(response.data.message || 'Failed to send message');
  } catch (error) {
    console.error('Error sending WhatsApp message:', error);
    throw error;
  }
};

/**
 * Send a WhatsApp media message
 * @param {Object} params - Media message parameters
 * @param {string} params.to - Recipient phone number
 * @param {string} params.mediaUrl - Media file URL
 * @param {string} params.mediaType - Media type (image, video, document, audio)
 * @param {string} params.caption - Optional caption for media
 * @param {string} params.tenantId - The tenant ID
 * @param {string} params.userId - The user ID
 * @returns {Promise<Object>} - Response with message ID
 */
export const sendWhatsAppMedia = async ({
  to,
  mediaUrl,
  mediaType,
  caption = '',
  tenantId,
  userId
}) => {
  try {
    const response = await axios.post(
      `${API_URL}/api/whatsapp/send-media`,
      {
        to,
        mediaUrl,
        mediaType,
        caption
      },
      {
        headers: {
          tenantid: tenantId,
          userid: userId
        }
      }
    );

    if (response.data.status === 'success') {
      return response.data;
    }
    throw new Error(response.data.message || 'Failed to send media');
  } catch (error) {
    console.error('Error sending WhatsApp media:', error);
    throw error;
  }
};

/**
 * Get WhatsApp configuration status
 * @param {string} tenantId - The tenant ID
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} - Configuration object
 */
export const getWhatsAppConfig = async (tenantId, userId) => {
  try {
    const response = await axios.get(`${API_URL}/api/whatsapp-config/config`, {
      headers: {
        tenantid: tenantId,
        userid: userId
      }
    });

    if (response.data.status === 'success') {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch config');
  } catch (error) {
    console.error('Error fetching WhatsApp config:', error);
    throw error;
  }
};

/**
 * Check if WhatsApp is configured and active
 * @param {string} tenantId - The tenant ID
 * @param {string} userId - The user ID
 * @returns {Promise<boolean>} - True if WhatsApp is active
 */
export const isWhatsAppActive = async (tenantId, userId) => {
  try {
    const config = await getWhatsAppConfig(tenantId, userId);
    return config.isActive === true;
  } catch (error) {
    return false;
  }
};

/**
 * Helper to build template components with variables
 * @param {Object} template - Template object from API
 * @param {Object} variables - Object with variable values (e.g., { body: ['John', '123'], header: ['image_url'] })
 * @returns {Array} - Components array ready for API
 */
export const buildTemplateComponents = (template, variables = {}) => {
  const components = [];

  template.components?.forEach((component) => {
    if (component.type === 'HEADER' && variables.header) {
      if (component.format === 'TEXT') {
        components.push({
          type: 'HEADER',
          parameters: variables.header.map((value) => ({
            type: 'text',
            text: value
          }))
        });
      } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(component.format)) {
        components.push({
          type: 'HEADER',
          parameters: [{
            type: component.format.toLowerCase(),
            [component.format.toLowerCase()]: { link: variables.header[0] }
          }]
        });
      }
    }

    if (component.type === 'BODY' && variables.body) {
      components.push({
        type: 'BODY',
        parameters: variables.body.map((value) => ({
          type: 'text',
          text: value
        }))
      });
    }

    if (component.type === 'BUTTONS' && variables.buttons) {
      variables.buttons.forEach((buttonValue, index) => {
        components.push({
          type: 'BUTTON',
          sub_type: component.buttons[index].type,
          index: index,
          parameters: [{
            type: 'text',
            text: buttonValue
          }]
        });
      });
    }
  });

  return components;
};

export default {
  fetchWhatsAppTemplates,
  fetchApprovedTemplates,
  sendWhatsAppTemplate,
  sendWhatsAppMessage,
  sendWhatsAppMedia,
  getWhatsAppConfig,
  isWhatsAppActive,
  buildTemplateComponents
};
